<script lang="ts">
  import {
    computePosition,
    flip,
    shift,
    offset,
    autoUpdate,
    type Placement,
  } from '@floating-ui/dom';
  import {
    dayCellPopoverState,
    enterPopover,
    leavePopover,
    hideDayCellPopover,
    registerFloatingElement,
    unregisterFloatingElement,
  } from '../lib/stores/dayCellPopover';
  import { openEventModal, openEventModalForEdit, draggedEvent } from '../lib/stores/ui';
  import { eventColor } from '../lib/eventColors';
  import { setCompactDragImage } from '../lib/dragImage';
  import {
    isMultiDayEvent,
    eventTotalDays,
    eventDayIndex,
    parseCalendarDate,
    formatCalendarDate,
    formatEventTimeRange,
    formatEventDuration,
    formatEventFullRange,
  } from '../lib/utils';
  import type { CalendarEvent } from '../lib/types';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';
  import { viewport } from '../lib/stores/viewport';

  const EVENT_TYPE_LABELS: Record<string, { icon: string; label: string }> = {
    appointment: { icon: '📅', label: 'Appointment' },
    travel: { icon: '✈️', label: 'Travel' },
    event: { icon: '🎉', label: 'Event' },
    deadline: { icon: '⏰', label: 'Deadline' },
  };

  function formatDateFull(dateStr: string): string {
    const d = parseCalendarDate(dateStr);
    return formatCalendarDate(d, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function formatDateShort(dateStr: string): string {
    const d = parseCalendarDate(dateStr);
    return formatCalendarDate(d, { month: 'short', day: 'numeric' });
  }

  let popoverState = $derived($dayCellPopoverState);

  // Floating UI positioning
  let floatingEl: HTMLDivElement | undefined = $state();
  let floatingStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let currentPlacement: Placement = $state('bottom-start');
  let autoUpdateCleanup: (() => void) | null = null;

  async function updatePosition(
    anchorEl: HTMLElement,
    floating: HTMLDivElement,
  ): Promise<Placement> {
    const result = await computePosition(anchorEl, floating, {
      placement: 'bottom-start',
      middleware: [
        offset(6),
        flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
        shift({ padding: 8 }),
      ],
    });
    currentPlacement = result.placement;
    floatingStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; width: 260px; max-height: 340px; visibility: visible;`;
    return result.placement;
  }

  // React to state changes: set up autoUpdate when popover opens, tear down when it closes
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: must call autoUpdate() on mount and return cleanup
  $effect(() => {
    // Clean up previous
    if (autoUpdateCleanup) {
      autoUpdateCleanup();
      autoUpdateCleanup = null;
    }

    if (popoverState && floatingEl) {
      const anchorEl = popoverState.anchorEl;

      // Start Floating UI auto-update loop
      autoUpdateCleanup = autoUpdate(anchorEl, floatingEl, () => {
        if (floatingEl) updatePosition(anchorEl, floatingEl);
      });

      // Position first, then register the floating element with the store.
      // The store will activate the safe polygon later — when the cursor
      // actually leaves the cell — using the cursor's exit coordinates
      // as the triangle origin.
      updatePosition(anchorEl, floatingEl).then((placement) => {
        if (floatingEl && popoverState) {
          const side = (placement.split('-')[0] || 'bottom') as 'top' | 'bottom' | 'left' | 'right';
          registerFloatingElement(floatingEl, side);
        }
      });
    } else {
      unregisterFloatingElement();
    }

    return () => {
      if (autoUpdateCleanup) {
        autoUpdateCleanup();
        autoUpdateCleanup = null;
      }
      unregisterFloatingElement();
    };
  });

  function handleAddEvent() {
    const dateStr = popoverState?.dateStr;
    hideDayCellPopover(true);
    if (dateStr) openEventModal(dateStr);
  }

  // Drag support from popover
  // We must set a custom drag image and defer popover close so the browser
  // can capture the drag before the source DOM element is removed.
  function handleDragStart(e: DragEvent, event: CalendarEvent) {
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', event.id);
      setCompactDragImage(e, event.title, event.type, isMultiDayEvent(event));
    }
    // Defer popover close to next frame so the browser captures the drag image
    // before the source element is removed from the DOM.
    requestAnimationFrame(() => {
      hideDayCellPopover(true);
    });
  }

  function handleDragEnd() {
    draggedEvent.set(null);
  }
</script>

<!-- Desktop: hover card anchored to the cell. Mobile: half-height bottom sheet. -->
<ResponsiveOverlay
  open={!!popoverState}
  onClose={() => hideDayCellPopover(true)}
  detents={['half', 'full']}
  initialDetent="half"
  title={popoverState ? formatDateFull(popoverState.dateStr) : undefined}
  label="Day events"
>
  {#snippet desktop(content)}
    {#if popoverState}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="day-popover"
        style={floatingStyle}
        bind:this={floatingEl}
        onmouseenter={enterPopover}
        onmouseleave={leavePopover}
      >
        <div class="popover-header">
          <div class="popover-date">{formatDateFull(popoverState.dateStr)}</div>
          {#if popoverState.events.length > 0}
            <span class="popover-count">{popoverState.events.length}</span>
          {/if}
        </div>
        {@render content()}
      </div>
    {/if}
  {/snippet}

  {#if popoverState}
    {@const { dateStr, events } = popoverState}
    {#if events.length > 0}
      <div class="popover-events">
        {#each events as event (event.id)}
          {@const timeRange = formatEventTimeRange(event)}
          {@const duration = formatEventDuration(event)}
          {@const fullRange = formatEventFullRange(event)}
          {@const isMultiDay = isMultiDayEvent(event)}
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="popover-event"
            draggable={!$viewport.isMobile}
            ondragstart={(e) => handleDragStart(e, event)}
            ondragend={handleDragEnd}
            role="button"
            tabindex="0"
            onclick={() => {
              hideDayCellPopover(true);
              openEventModalForEdit(event);
            }}
            onkeydown={(e) => {
              if (e.key === 'Enter') {
                hideDayCellPopover(true);
                openEventModalForEdit(event);
              }
            }}
          >
            <div class="event-accent" style="background: {eventColor(event.type)}"></div>
            <div class="event-content">
              <div class="event-title">{event.title}</div>
              <div class="event-meta">
                <span class="event-type">
                  {EVENT_TYPE_LABELS[event.type]?.icon ?? ''}
                  {EVENT_TYPE_LABELS[event.type]?.label ?? event.type}
                </span>
                {#if fullRange}
                  <!-- Multi-day timed event: show full date+time range -->
                  <span class="event-range">{fullRange}</span>
                  {@const pos = eventDayIndex(event, dateStr)}
                  <span class="event-days">Day {pos.dayIndex} of {pos.totalDays}</span>
                {:else if timeRange}
                  <!-- Same-day timed event -->
                  <span class="event-time">{timeRange}</span>
                  {#if duration}
                    <span class="event-duration">({duration})</span>
                  {/if}
                {:else if isMultiDay}
                  <!-- Multi-day all-day event -->
                  {@const pos = eventDayIndex(event, dateStr)}
                  <span class="event-range"
                    >{formatDateShort(event.date)} → {formatDateShort(
                      event.endDate ?? event.date,
                    )}</span
                  >
                  <span class="event-days">Day {pos.dayIndex} of {pos.totalDays}</span>
                {/if}
              </div>
              {#if event.notes}
                <div class="event-notes">{event.notes}</div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <div class="popover-empty">No events</div>
    {/if}

    <div class="popover-footer">
      <button class="popover-add" onclick={handleAddEvent}> + Add event </button>
    </div>
  {/if}
</ResponsiveOverlay>

<style>
  .day-popover {
    z-index: 200;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.15),
      0 4px 10px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    pointer-events: auto;
  }

  .popover-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .popover-date {
    font-size: 0.75rem;
    font-weight: 700;
    color: #1f2937;
  }

  .popover-count {
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    background: #e5e7eb;
    padding: 0.0625rem 0.4375rem;
    border-radius: 9999px;
    min-width: 1.125rem;
    text-align: center;
  }

  .popover-events {
    flex: 1;
    overflow-y: auto;
    padding: 0.375rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .popover-event {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.4375rem 0.5rem;
    background: #f9fafb;
    border-radius: 0.375rem;
    cursor: grab;
    transition:
      background 0.1s,
      border-color 0.1s;
    border: 1px solid transparent;
  }

  .popover-event:hover {
    background: #f0f9ff;
    border-color: #bfdbfe;
  }

  .popover-event:active {
    cursor: grabbing;
  }

  .event-accent {
    width: 3px;
    border-radius: 2px;
    flex-shrink: 0;
    min-height: 1.25rem;
  }

  .event-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .event-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
    word-break: break-word;
  }

  .event-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .event-type {
    font-size: 0.6875rem;
    color: #6b7280;
  }

  .event-time {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
  }

  .event-duration {
    font-size: 0.625rem;
    color: #9ca3af;
  }

  .event-range {
    font-size: 0.6875rem;
    color: #9ca3af;
  }

  .event-days {
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0 0.25rem;
    border-radius: 0.1875rem;
  }

  .event-notes {
    font-size: 0.6875rem;
    color: #4b5563;
    line-height: 1.4;
    white-space: pre-wrap;
    max-height: 2.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    border-top: 1px solid #f3f4f6;
    padding-top: 0.25rem;
    margin-top: 0.125rem;
  }

  .popover-empty {
    padding: 0.75rem;
    text-align: center;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .popover-footer {
    padding: 0.375rem 0.75rem;
    border-top: 1px solid #e5e7eb;
  }

  .popover-add {
    font-size: 0.6875rem;
    color: #3b82f6;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-weight: 500;
  }

  .popover-add:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  /* Mobile: the same content in a bottom sheet (ResponsiveOverlay). */
  :global(.mobile-sheet-panel) .popover-events {
    padding: 0.5rem 0.75rem;
    gap: 0.375rem;
  }

  :global(.mobile-sheet-panel) .popover-event {
    min-height: 3rem;
    padding: 0.625rem 0.75rem;
    cursor: pointer;
  }

  :global(.mobile-sheet-panel) .event-title {
    font-size: 0.9375rem;
  }

  :global(.mobile-sheet-panel) .event-type,
  :global(.mobile-sheet-panel) .event-time,
  :global(.mobile-sheet-panel) .event-range {
    font-size: 0.8125rem;
  }

  :global(.mobile-sheet-panel) .event-duration,
  :global(.mobile-sheet-panel) .event-days,
  :global(.mobile-sheet-panel) .event-notes {
    font-size: 0.75rem;
  }

  :global(.mobile-sheet-panel) .event-notes {
    max-height: none;
  }

  :global(.mobile-sheet-panel) .popover-empty {
    padding: 1.5rem 1rem;
    font-size: 0.875rem;
  }

  :global(.mobile-sheet-panel) .popover-footer {
    padding: 0.25rem 0.75rem 0.5rem;
  }

  :global(.mobile-sheet-panel) .popover-add {
    min-height: var(--mobile-tap);
    font-size: 0.9375rem;
  }
</style>
